import { inputHandler } from './input';
import { validator } from './regex';
import { isMobileScreen, debounce } from './utils';
import { BREAKPOINTS } from './constants';

let footerTopEventListenerAdded = false;

const isValid = input => {
    return (
        !input?.classList.contains('input_error') &&
        validator(input?.value, input?.type)
    );
};

const displayErrorMessage = message => {
    const errorElement = document.querySelector('.input_error-msg');
    errorElement.textContent = message;
    errorElement.classList.remove('hidden');
};

const fetchData = async (apiUrl, token, email, recaptchaToken) => {
    try {
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                RequestVerificationToken: token
            },
            body: JSON.stringify({
                emailAddress: email,
                recaptchaToken: recaptchaToken
            })
        });

        if (!response.ok) {
            throw new Error('Failed to fetch data');
        }

        const responseData = await response.json();
        return { ok: response.ok, data: responseData };
    } catch (error) {
        console.error('Failed to fetch data:', error);
        return {
            ok: false,
            error: 'Invalid reCAPTCHA verification. Please try again.'
        };
    }
};

const handleSuccessfulSubmission = (
    button,
    inputContainer,
    input,
    errorElement
) => {
    const wrapper = inputContainer.firstElementChild;
    button.classList.add('hidden');
    wrapper.classList.replace('input--flush', 'input--flush--secondary');
    input.type = 'text';
    input.value = 'Thanks for joining our mailer!';
    input.setAttribute('readonly', '');
    input.removeAttribute('required');
    errorElement.classList.add('hidden');
};

const submitNewsletterForm = async (
    button,
    inputContainer,
    input,
    footer,
    recaptchaToken,
    errorElement
) => {
    if (!isValid(input)) return;

    const apiUrl = footer.getAttribute('data-api-url');
    const token = document.querySelector(
        'input[name="__RequestVerificationToken"]'
    )?.value;

    const { ok, data, error } = await fetchData(
        apiUrl,
        token,
        input.value,
        recaptchaToken
    );

    if (!ok) {
        displayErrorMessage(
            data.message ?? error ?? 'Failed to subscribe to the newsletter.'
        );
    } else {
        handleSuccessfulSubmission(button, inputContainer, input, errorElement);
    }
};

const executeRecaptcha = (
    button,
    inputContainer,
    input,
    footer,
    errorElement
) => {
    if (typeof grecaptcha === 'undefined') return;

    grecaptcha.ready(() => {
        grecaptcha
            .execute('6LfeBDMnAAAAAO9F6K-Tbv7Pc8oyCIZfWVyjimYx', {
                action: 'newsletter_signup'
            })
            .then(token => {
                submitNewsletterForm(
                    button,
                    inputContainer,
                    input,
                    footer,
                    token,
                    errorElement
                );
            })
            .catch(error => {
                console.error('Error executing reCAPTCHA:', error);
                displayErrorMessage(
                    'Error executing reCAPTCHA. Please refresh the page and try again.'
                );
            });
    });
};

const onButtonClick = async (
    button,
    inputContainer,
    input,
    footer,
    errorElement
) => {
    if (!isValid(input)) {
        return;
    }
    
    executeRecaptcha(button, inputContainer, input, footer, errorElement);
};

const toggleListVisibility = listElement => {
    listElement.classList.toggle('show');
};

const expandListHandler = (targetList, lists = []) => {
    lists.forEach(item => {
        const listElement = item.lastElementChild;
        if (targetList !== listElement) return;
        toggleListVisibility(listElement);
    });
};

const updateArrowIcon = (lists, current) =>
    lists?.forEach(item => {
        const el = item?.firstElementChild?.lastElementChild;

        if (current === el) {
            el.classList.toggle('icon-arrow-down-dropdown');
            el.classList.toggle('icon-arrow-up-dropdown');
        } else {
            el.classList.replace(
                'icon-arrow-up-dropdown',
                'icon-arrow-down-dropdown'
            );
        }
    });

const handleExpandItems = footerTop => {
    if (!footerTop || !isMobileScreen(BREAKPOINTS.LG)) return;
    const lists = footerTop.querySelectorAll('.expand');

    const toggleExpand = e => {
        const isLink = e.target.tagName === 'A' || e.target.closest('a');
        if (isLink || !isMobileScreen(BREAKPOINTS.LG)) return;
        
        e.preventDefault();
        const targetEl = e.target.closest('.expand');
        const targetList = targetEl?.lastElementChild;
        if (!targetList) return;
        const el = targetEl?.firstElementChild?.lastElementChild;
        updateArrowIcon(lists, el);
        expandListHandler(targetList, lists);
    };

    if (!footerTop || footerTopEventListenerAdded) return;

    footerTop.addEventListener('click', e => toggleExpand(e));
    footerTop.addEventListener('keyup', e => {
        if (e.key !== 'Tab') toggleExpand(e);
    });

    footerTopEventListenerAdded = true;
};

const onFooterLoad = () => {
    const footer = document.getElementById('footer');
    if (!footer) return;

    const inputContainer = footer.querySelector('[data-input-field]');
    const input = inputContainer.querySelector('input');
    const button = inputContainer.querySelector('[data-newsletter-button]');
    const expand = footer.querySelector('.footer__top');
    const errorElement = document.querySelector('.input_error-msg');

    inputHandler(inputContainer);
    handleExpandItems(expand);

    const debouncedHandleExpandItems = debounce(() =>
        handleExpandItems(expand)
    );

    button?.addEventListener('click', () => {
        onButtonClick(button, inputContainer, input, footer, errorElement);
    });
    input?.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === '') {
            onButtonClick(button, inputContainer, input, footer, errorElement);
        }
    });
    window.addEventListener('resize', debouncedHandleExpandItems);
};

export default onFooterLoad;
